import { useEffect } from "react";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useAuth } from "../context/AuthContext";
import { homePathForRole, homeLabelForRole } from "../lib/homePath";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { TopBar } from "./kit";
import { isToken, splitTokens } from "../lib/payCopy";

/**
 * The frame the three pay steps share (#1036, D199.4, D26, D35): set-up's
 * white bar with the bell, contrast and account menus, then the flow row.
 * No Back, no destination row. The page owns its screen (the chromeless
 * override), the way `DeleteAccountPage` does.
 */
export function PlaceholderToken({ children }) {
  return (
    <span
      data-testid="placeholder-token"
      data-placeholder="true"
      style={{ borderBottom: "1px dashed var(--nw-ink-muted)", whiteSpace: "nowrap" }}
    >
      {children}
    </span>
  );
}

/** Text with each `[TOKEN]` drawn as a visible placeholder. */
export function WithTokens({ text }) {
  return splitTokens(text).map((part, i) => (isToken(part) ? <PlaceholderToken key={i}>{part}</PlaceholderToken> : <span key={i}>{part}</span>));
}

export function PayFlow({ steps, current, testId }) {
  return (
    <ol
      data-testid={testId}
      aria-label="Where you are"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        margin: 0,
        padding: "10px 12px",
        listStyle: "none",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: 10,
      }}
    >
      {steps.map((label, i) => (
        <li key={label} aria-current={i === current ? "step" : undefined} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: i === current ? 700 : 600, color: i === current ? "var(--nw-navy)" : "var(--nw-ink-muted)" }}>{label}</span>
          {i < steps.length - 1 && (
            <span aria-hidden="true" style={{ fontSize: 11, color: "var(--nw-line-strong)" }}>
              ›
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

export default function PayChrome({ screenTestId, flow, current, flowTestId, inert = false, children }) {
  const { setActive: setChromeless } = useChromelessOverride();
  const { user } = useAuth();

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={screenTestId.replace(/-new-screen$/, "-page")}>
      <span data-testid={screenTestId} className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />
      <TopBar
        variant="white"
        homeHref={homePathForRole(user?.role)}
        homeLabel={`CricketCompass — go to ${homeLabelForRole(user?.role)}`}
        endControls={
          <span {...(inert ? { inert: true } : {})} style={{ display: "contents" }}>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu surface="white" />
            <AccountMenu empty surface="white" />
          </span>
        }
        data-testid="pay-topbar"
      />
      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "20px 20px 24px" }}>
        <PayFlow steps={flow} current={current} testId={flowTestId} />
        {children}
      </div>
    </div>
  );
}
